
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/assessment';
import { PackageType } from '@/types/payment';

interface UsePaymentHandlerProps {
  userId?: string;
  userProfile?: UserProfile | null;
  packageType: PackageType;
  finalAmount: number;
  couponCode: string;
  discount: number;
  paymentType: 'order' | 'booking';
  razorpayReady?: boolean;
}

export const usePaymentHandler = ({
  userId,
  userProfile,
  packageType,
  finalAmount,
  couponCode,
  discount,
  paymentType,
  razorpayReady = false
}: UsePaymentHandlerProps) => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleProceedToPayment = async () => {
    if (!userId || !userProfile) {
      toast.error('Please log in to continue');
      navigate('/login');
      return;
    }

    // Free-order bypass: 100% coupon discount → skip Razorpay entirely
    if (paymentType === 'order' && finalAmount <= 0 && discount > 0 && couponCode) {
      setIsProcessing(true);
      try {
        const { data, error } = await supabase.functions.invoke('create-free-order', {
          body: { packageType, couponCode },
        });
        if (error || !data?.success) {
          throw new Error(error?.message || data?.error || 'Failed to redeem coupon');
        }
        toast.success('Coupon redeemed! Free access granted.');
        localStorage.setItem('current_order_id', data.orderId);
        navigate(`/payment-success?order=${data.orderId}`);
      } catch (err: any) {
        console.error('Free order error:', err);
        toast.error(err.message || 'Failed to redeem coupon');
        setIsProcessing(false);
      }
      return;
    }

    // Verify Razorpay is loaded for paid flow
    if (!razorpayReady || !window.Razorpay) {
      console.error("Razorpay SDK not loaded yet");
      toast.error('Payment system is still loading. Please try again in a moment.');
      return;
    }

    setIsProcessing(true);

    try {
      // For bookings, redirect to the booking payment page
      if (paymentType === 'booking') {
        console.log('Redirecting to booking payment page');
        navigate(`/payment?type=booking`);
        return;
      }

      // For orders, create the order and proceed with payment
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: {
          userId: userId,
          amount: finalAmount,
          packageType,
          couponCode: discount > 0 ? couponCode : undefined,
          name: userProfile.name,
          email: userProfile.email,
          phone: userProfile.phone
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to create payment');
      }

      if (!data || !data.razorpayOrderId) {
        throw new Error('Invalid response from payment service');
      }

      const options = {
        key: data.keyId,
        amount: data.amount * 100,
        currency: data.currency,
        name: 'Career Guide Kerala',
        description: `${packageType === 'silver' ? 'Silver' : 'Gold'} Package`,
        order_id: data.razorpayOrderId,
        handler: async function (response: any) {
          await handlePaymentSuccess(
            data.orderId,
            response.razorpay_payment_id,
            response.razorpay_order_id,
            response.razorpay_signature
          );
        },
        prefill: {
          name: userProfile.name,
          email: userProfile.email,
          contact: userProfile.phone
        },
        theme: {
          color: '#1976D2'
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment processing error:', error);
      toast.error(error.message || 'Payment processing failed');
      setIsProcessing(false);
    }
  };

  const handlePaymentSuccess = async (
    orderId: string,
    razorpayPaymentId: string,
    razorpayOrderId: string,
    razorpaySignature: string
  ) => {
    try {
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: {
          orderId,
          razorpayPaymentId,
          razorpayOrderId,
          razorpaySignature
        }
      });

      if (error || !data.success) {
        throw new Error(error?.message || 'Payment verification failed');
      }

      toast.success('Payment successful!');
      
      // Store the order ID in localStorage to ensure it's available immediately
      localStorage.setItem('current_order_id', orderId);
      console.log('Payment handler: Stored order ID in localStorage:', orderId);
      
      // Navigate to payment success page with order parameter
      navigate(`/payment-success?order=${orderId}`);
      
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error(error.message || 'Payment verification failed');
      setIsProcessing(false);
    }
  };

  return {
    isProcessing,
    setIsProcessing,
    handleProceedToPayment,
  };
};

export default usePaymentHandler;
